/*=========================================================================================
  File Name: actions.js
  Namespace: labs
  Description: Vuex Store - Lab actions
==========================================================================================*/

import {
  createLab,
  createObjective,
  deleteObjective,
  editLab,
  fetchObjectives,
  getLab,
  getLabsCategory,
  listLabs,
  searchLabsByIds,
  listLabsCategories,
  updateObjective,
} from '@/services/labs.service'
import { assignTaxonomyToLab } from '@/services/taxonomies.service'
import { adaptLabToStore } from '@/services/adapters/labAdapter.js'
import { getLabManualID } from '@/services/collections.service.js'
import { shortenUUIDs } from '@/utils/utils'

const buildSearchLabsParams = (params, company) => {
  const baseParams = {
    ...params,
    company,
  }
  const idsCount = Array.isArray(baseParams.ids)
    ? baseParams.ids.length
    : baseParams.ids
    ? 1
    : 0
  const pageSize =
    baseParams.page_size || (idsCount ? idsCount.toString() : undefined)

  return {
    ...baseParams,
    page_size: pageSize,
  }
}

const actions = {
  async getLabAction({ commit }, { labId }) {
    const response = await getLab({
      labId,
    })
    if (response) {
      commit('STORE_CURRENT_LAB', response)
    }
    return response
  },
  async getAllLabs({ commit, rootGetters }, params = {}) {
    if (params.ids && params.ids.length > 20) {
      params.ids = shortenUUIDs(params.ids)
    }
    params.company = rootGetters['app/getSelectedCompanyName']
    const response = await listLabs(params)
    if (response) {
      commit('STORE_LIST_LABS', response)
    }
    return response
  },
  async getAllLabsById({ commit, rootGetters }, params = {}) {
    const searchParams = buildSearchLabsParams(
      params,
      rootGetters['app/getSelectedCompanyName']
    )

    const response = await searchLabsByIds(searchParams)
    if (response) {
      commit('STORE_LIST_LABS', response)
    }
    return response
  },
  async getLabsById({ rootGetters }, params = {}) {
    params.company = rootGetters['app/getSelectedCompanyName']

    const response = await listLabs(params)

    return response
  },
  async searchLabsById({ rootGetters }, params = {}) {
    const searchParams = buildSearchLabsParams(
      params,
      rootGetters['app/getSelectedCompanyName']
    )

    return await searchLabsByIds(searchParams)
  },
  async saveLabToApi({ commit, getters, rootState }) {
    let response
    if (getters.getCurrentLab.id) {
      response = await editLab({
        ...getters.getCurrentLab,
        labId: getters.getCurrentLab.id,
        company: rootState.app.company,
      })
    } else {
      response = await createLab({
        ...getters.getCurrentLab,
        company: rootState.app.company,
      })
    }
    commit('STORE_CURRENT_LAB', response)
    return response
  },
  async fetchObjectives({ commit }, labId) {
    const response = await fetchObjectives({ labId })

    if (response) commit('STORE_LIST_OBJECTIVES', response.results)

    return response
  },
  async saveObjectiveToApi({ commit }, payload) {
    let response

    if (payload.id) {
      response = await updateObjective(payload)
    } else {
      response = await createObjective(payload)
    }

    commit('STORE_OBJECTIVE', response)

    return response
  },
  async deleteObjectiveToApi({ commit }, payload) {
    let response

    await deleteObjective({ id: payload.id, lab: payload.lab })

    commit('DELETE_OBJECTIVE', payload)

    return response
  },
  saveLabToEdit({ commit }, lab) {
    commit(
      'STORE_CURRENT_LAB',
      adaptLabToStore({
        data: lab,
      })
    )
  },
  saveLabToEntries({ commit }, lab) {
    commit('STORE_LAB_TO_LIST', {
      [lab.id]: lab,
    })
  },
  resetCurrentLab({ commit }) {
    commit('RESET_CURRENT_LAB')
  },
  async getLabsCategory({ commit }, categoryId) {
    const response = await getLabsCategory({
      categoryId: categoryId,
    })
    if (response) {
      commit('STORE_CATEGORY_LIST', response)
    }
    return response
  },
  async getAllLabsCategories({ commit }, params = {}) {
    params.page_size = 9999
    const response = await listLabsCategories(params)
    if (response) {
      commit('STORE_CATEGORY_LIST', response)
    }
    return response
  },
  resetCurrentCategories({ commit }) {
    commit('RESET_CATEGORIES')
  },
  async getLabByManualId({ commit }, argusId) {
    const response = await getLabManualID({ argusId })
    if (response && response.name) {
      const { name, overview, tasks, solution, sdn, namespace, flags } =
        response
      const labArgusResponse = {
        name,
        description: overview,
        tasks,
        solutions: solution,
        lab_type: 'pta',
        metadata: {
          pta_manual_id: argusId,
          pta_sdn: sdn,
          pta_namespace: namespace,
        },
      }
      if (flags) {
        labArgusResponse.flags = flags
      }
      commit('STORE_CURRENT_LAB', labArgusResponse)
      return response
    }
  },
  async assignTaxonomiesToLab({ commit }, { contentId, addedSkills }) {
    if (addedSkills && addedSkills.length > 0 && contentId) {
      await Promise.allSettled(
        addedSkills.map(async skill => {
          const response = await assignTaxonomyToLab({
            labId: contentId,
            skillId: skill.skillId
          })
          if (response) {
            commit('ADD_TO_CURRENT_LAB_TAXONOMY_SKILLS', {skillId: skill.skillId, taxonomyPath: skill.taxonomyPath})
            commit('taxonomy/DELETE_LAB_TAXONOMY_SKILL', { contentId, skillId: skill.skillId }, { root: true })
          }
        })
      )
    }
  }
}

export default actions
